import ZenProperties from '../jupiter.js';


/**
 * Extra structure definitions added to the flat ZenProperties map.
 *
 * All entries here follow the same shape as jupiter.js — a top-level
 *   ZenProperties[NAME] = {
 *       name, byteLength, parameters: { ... }
 *   }
 * There is no distinction between "block" and "group" any more: an entry is
 * a composite structure when its parameters reference other entries via the
 * `block` field, and a leaf structure otherwise.
 */

ZenProperties.NAME = {
    name: "NAME",
    byteLength: 16,
    parameters: {
        NAME_1: {
            id: "NAME_1",
            description: "Name",
            byteOffset: 0,
            byteLength: 16,
            dataRange: [32, 127],
            initValue: 32,
            values: null,
            displayMeasurement: "ASCII"
        }
    }
};

//---------------------------------------------------------------
// MC101 / MC707 / MV-1
//---------------------------------------------------------------

ZenProperties.CLIPINFO = {
    name: "ClipInfo",
    byteLength: 23616,
    parameters: {
        NAME_1: {
            id: "NAME_1",
            description: "Clip Name",
            byteOffset: 0,
            byteLength: 16,
            dataRange: null,
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        TRACK_TYPE: {
            id: "TRACK_TYPE",
            description: "Track Type",
            byteOffset: 18,
            byteLength: 1,
            dataRange: [-1, 2],
            initValue: -1,
            values: ["None", "Synth", "Drum", "DrumAndComp"],
            displayMeasurement: null
        },
        TX_PROGRAM_CHANGE_NUMBER: {
            id: "TX_PROGRAM_CHANGE_NUMBER",
            description: "Tx Program Change Number",
            byteOffset: 24,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        TX_PROGRAM_CHANGE_LSB: {
            id: "TX_PROGRAM_CHANGE_LSB",
            description: "Tx Program Change LSB",
            byteOffset: 25,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        TX_PROGRAM_CHANGE_MSB: {
            id: "TX_PROGRAM_CHANGE_MSB",
            description: "Tx Program Change MSB",
            byteOffset: 26,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        SCALE: {
            id: "SCALE",
            description: "Scale",
            byteOffset: 768,
            byteLength: 1,
            dataRange: [0, 5],
            initValue: 0,
            values: ["1/8", "1/16", "1/32", "1/4T", "1/8T", "1/16T"],
            displayMeasurement: null
        },
        STEP_LEN: {
            id: "STEP_LEN",
            description: "Step Length",
            byteOffset: 769,
            byteLength: 2,
            dataRange: [1, 512],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        SHUFFLE: {
            id: "SHUFFLE",
            description: "Shuffle",
            byteOffset: 776,
            byteLength: 1,
            dataRange: [-50, 50],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        DIRECTION: {
            id: "DIRECTION",
            description: "Direction",
            byteOffset: 777,
            byteLength: 1,
            dataRange: [0, 4],
            initValue: 0,
            values: ["Forward", "Reverse", "Forward+Reverse", "Inverse", "Random"],
            displayMeasurement: null
        },
        NEXT_CLIP: {
            id: "NEXT_CLIP",
            description: "Next Clip",
            byteOffset: 779,
            byteLength: 1,
            dataRange: [-2, 15],
            initValue: -2,
            values: [
                "Stay", "Stop",
                "Clip1", "Clip2", "Clip3", "Clip4", "Clip5", "Clip6", "Clip7", "Clip8",
                "Clip9", "Clip10", "Clip11", "Clip12", "Clip13", "Clip14", "Clip15", "Clip16"
            ],
            displayMeasurement: null
        }
    }
};

ZenProperties.TRACKINFO = {
    name: "TrackInfo",
    byteLength: 256,
    parameters: {
        TRACK_TYPE: {
            id: "TRACK_TYPE",
            description: "Track Type",
            byteOffset: 4,
            byteLength: 1,
            dataRange: [-1, 3],
            initValue: 0,
            values: ["None", "Tone", "Drum", "Looper"],
            displayMeasurement: null
        },
        SOURCE: {
            id: "SOURCE",
            description: "Sound Source",
            byteOffset: 5,
            byteLength: 1,
            dataRange: [0, 1],
            initValue: 0,
            values: ["Track", "Clip"],
            displayMeasurement: null
        },
        OUTPUT: {
            id: "OUTPUT",
            description: "Output Source",
            byteOffset: 19,
            byteLength: 1,
            dataRange: [0, 1],
            initValue: 0,
            values: ["Main Mix", "Assign Out"],
            displayMeasurement: null
        },
        TRACK_COLOR: {
            id: "TRACK_COLOR",
            description: "Track Colour",
            byteOffset: 36,
            byteLength: 1,
            dataRange: [0, 19],
            initValue: 0,
            values: ["Red", "Orange", "Yellow", "Green", "Blue", "Purple", "Pink", "White", "Sky Blue", "P.Yellow", "P.Blue", "P.Pink", "L.Red", "L.Orange", "L.Yellow", "L.Green", "P.Green", "L.Sky Blue", "L.Blue", "L.Purple"],
            displayMeasurement: null
        }
    }
};

ZenProperties.SMPd = {
    name: "SMPd",
    byteLength: 48,
    parameters: {
        U1: {
            id: "U1",
            description: "Unknown 1",
            byteOffset: 4,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        TOTAL_SAMPLE_DATA_SIZE: {
            id: "TOTAL_SAMPLE_DATA_SIZE",
            description: "Total Sample Data Size",
            byteOffset: 8,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        CHANNEL_SAMPLE_DATA_SIZE: {
            id: "CHANNEL_SAMPLE_DATA_SIZE",
            description: "Size of actual data for channel",
            byteOffset: 12,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        NAME_1: {
            id: "NAME_1",
            description: "Sample Name",
            byteOffset: 16,
            byteLength: 16,
            dataRange: [0, 0],
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        SAMPLE_NUMBER: {
            id: "SAMPLE_NUMBER",
            description: "Sample Number",
            byteOffset: 32,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        STEREO_FLAG: {
            id: "STEREO_FLAG",
            description: "Stereo Flag",
            byteOffset: 33,
            byteLength: 1,
            dataRange: [0, 128],
            initValue: 0,
            values: { 0: "Mono", 128: "Stereo" },
            displayMeasurement: null
        },
        SAMPLE_RATE: {
            id: "SAMPLE_RATE",
            description: "Sample Rate",
            byteOffset: 36,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        U3: {
            id: "U3",
            description: "Unknown 3",
            byteOffset: 40,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        U4: {
            id: "U4",
            description: "Unknown 4",
            byteOffset: 44,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        }
    }
};

ZenProperties.SAMPLE_INFO = {
    name: "SampleInfo",
    byteLength: 84,
    parameters: {
        NAME_1: {
            id: "NAME_1",
            description: "Sample Name",
            byteOffset: 0,
            byteLength: 16,
            dataRange: [0, 255],
            initValue: "                ",
            values: null,
            displayMeasurement: null
        },
        PLAY_MODE: {
            id: "PLAY_MODE",
            description: "Play Mode",
            byteOffset: 64,
            byteLength: 1,
            dataRange: [0, 3],
            initValue: 1,
            values: ["FWD", "ONE-SHOT", "REV", "REV-ONE"],
            displayMeasurement: null
        },
        VOLUME: {
            id: "VOLUME",
            description: "Sample Volume",
            byteOffset: 65,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 127,
            values: null,
            displayMeasurement: null
        },
        TUNE: {
            id: "TUNE",
            description: "Sample Tune",
            byteOffset: 66,
            byteLength: 1,
            dataRange: [-127, 128],
            initValue: 0,
            values: null,
            displayMeasurement: "cents"
        },
        GAIN: {
            id: "GAIN",
            description: "Sample Gain",
            byteOffset: 68,
            byteLength: 1,
            dataRange: [0, 2],
            initValue: 0,
            values: ["0db", "6db", "12db"],
            displayMeasurement: null
        },
        ORG_KEY: {
            id: "ORG_KEY",
            description: "Original Key",
            byteOffset: 69,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: 60,
            values: null,
            displayMeasurement: "key"
        },
        START_FRAME: {
            id: "START_FRAME",
            description: "Sample Start Frame",
            byteOffset: 72,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        LOOP_FRAME: {
            id: "LOOP_FRAME",
            description: "Sample Loop Frame",
            byteOffset: 76,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 0,
            values: null,
            displayMeasurement: null
        },
        END_FRAME: {
            id: "END_FRAME",
            description: "Sample End Frame",
            byteOffset: 80,
            byteLength: 4,
            dataRange: [0, 4294967295],
            initValue: 88200,
            values: null,
            displayMeasurement: null
        }
    }
};

ZenProperties.MCUSD = {
    name: "MCUSD",
    byteLength: 16,
    parameters: {
        OFFSET: { id: "OFFSET", description: "Data offset", byteOffset: 0, byteLength: 4 },
        LENGTH: { id: "LENGTH", description: "Byte length", byteOffset: 4, byteLength: 4 },
        U1:     { id: "U1",     description: "Unknown 1",   byteOffset: 8, byteLength: 4 },
        U2:     { id: "U2",     description: "Unknown 2",   byteOffset: 12, byteLength: 4 }
    }
};

//---------------------------------------------------------------
// SH-4d
//---------------------------------------------------------------

ZenProperties.PATC_HEADER = {
    name: "PATcHeader",
    byteLength: 16,
    parameters: {
        UNKNOWN: {
            id: "UNKNOWN",
            description: "Unknown bytes",
            byteOffset: 0,
            byteLength: 6,
            dataRange: null,
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        FLAG1: {
            id: "FLAG1",
            description: "Flag1 (6 for most, 7 for Wavetable/Drawing)",
            byteOffset: 6,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        UNKNOWN2: {
            id: "UNKNOWN2",
            description: "UNKNOWN",
            byteOffset: 7,
            byteLength: 1,
            dataRange: null,
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        MODEL: {
            id: "MODEL",
            description: "Model Type",
            byteOffset: 8,
            byteLength: 1,
            dataRange: [16, 23],
            initValue: null,
            values: [
                "SH-4d", "SH-3d", "Sync", "SH-101",
                "Cross FM", "Ring", "Wavetable", "Drawing",
                "Chord", "Juno 106", "PCM", "Scep", "Harmonic"
            ],
            displayMeasurement: null
        },
        UNKNOWN3: {
            id: "UNKNOWN3",
            description: "UNKNOWN",
            byteOffset: 9,
            byteLength: 7,
            dataRange: null,
            initValue: null,
            values: null,
            displayMeasurement: null
        }
    }
};

ZenProperties.PATTERNINFO_SH4D = {
    name: "PatternInfoSH4D",
    byteLength: 32,
    parameters: {
        PATTERN_LEVEL: {
            id: "PATTERN_LEVEL",
            description: "Pattern Level",
            byteOffset: 0,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 100,
            values: null,
            displayMeasurement: "dB"
        },
        VOICE_RESERVE_P1: { id: "VOICE_RESERVE_P1", description: "Voice Reserve Part 1", byteOffset: 21, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        VOICE_RESERVE_P2: { id: "VOICE_RESERVE_P2", description: "Voice Reserve Part 2", byteOffset: 21, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        VOICE_RESERVE_P3: { id: "VOICE_RESERVE_P3", description: "Voice Reserve Part 3", byteOffset: 21, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        VOICE_RESERVE_P4: { id: "VOICE_RESERVE_P4", description: "Voice Reserve Part 4", byteOffset: 21, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        VOICE_RESERVE_P5: { id: "VOICE_RESERVE_P5", description: "Voice Reserve Rhythm", byteOffset: 21, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        CHAIN_LENGTH: { id: "CHAIN_LENGTH", description: "Chain Length", byteOffset: 29, byteLength: 1, dataRange: [0, 128], initValue: 0, values: null, displayMeasurement: null },
        NEXT_PATTERN: {
            id: "NEXT_PATTERN",
            description: "Next Pattern",
            byteOffset: 30,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 0,
            values: [
                "Next",
                "01-01","01-02","01-03","01-04","01-05","01-06","01-07","01-08","01-09","01-10","01-11","01-12","01-13","01-14","01-15","01-16",
                "02-01","02-02","02-03","02-04","02-05","02-06","02-07","02-08","02-09","02-10","02-11","02-12","02-13","02-14","02-15","02-16",
                "03-01","03-02","03-03","03-04","03-05","03-06","03-07","03-08","03-09","03-10","03-11","03-12","03-13","03-14","03-15","03-16",
                "04-01","04-02","04-03","04-04","04-05","04-06","04-07","04-08","04-09","04-10","04-11","04-12","04-13","04-14","04-15","04-16",
                "05-01","05-02","05-03","05-04","05-05","05-06","05-07","05-08","05-09","05-10","05-11","05-12","05-13","05-14","05-15","05-16",
                "06-01","06-02","06-03","06-04","06-05","06-06","06-07","06-08","06-09","06-10","06-11","06-12","06-13","06-14","06-15","06-16",
                "07-01","07-02","07-03","07-04","07-05","07-06","07-07","07-08","07-09","07-10","07-11","07-12","07-13","07-14","07-15","07-16",
                "08-01","08-02","08-03","08-04","08-05","08-06","08-07","08-08","08-09","08-10","08-11","08-12","08-13","08-14","08-15","08-16"
            ],
            displayMeasurement: null
        }
    }
};

ZenProperties.PARTINFO_SH4D = {
    name: "PartInfoSH4D",
    byteLength: 88,
    parameters: {
        BANK: {
            id: "BANK",
            description: "Bank",
            byteOffset: 11,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: 0,
            values: {
                0: "User",
                64: "SH4-D 2", 65: "SH4-D 3", 66: "SH4-D 4", 67: "SH4-D 5",
                68: "SH-3d 1", 69: "Sync",    70: "SH-101d", 71: "Juno 106",
                72: "Cross FM", 73: "Ring",   74: "Wavetable", 75: "Chord",
                76: "Drawing", 78: "PCM",     79: "Scep",    80: "Harmonic"
            },
            displayMeasurement: null
        },
        PATCH: {
            id: "PATCH",
            description: "Patch Number",
            byteOffset: 12,
            byteLength: 1,
            dataRange: [0, 127],
            initValue: 0,
            values: null,
            displayModifier: "SH4dPatchNumber"
        },
        LEVEL: { id: "LEVEL", description: "Part Level", byteOffset: 13, byteLength: 1, dataRange: [0, 127], initValue: 100, values: null, displayMeasurement: "" },
        PAN:   { id: "PAN",   description: "Pan",        byteOffset: 14, byteLength: 1, dataRange: [-64, 63], initValue: 0, values: null, displayMeasurement: null },
        CHORUS:{ id: "CHORUS",description: "Chorus Send",byteOffset: 34, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        REVERB:{ id: "REVERB",description: "Reverb Send",byteOffset: 35, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        DELAY: { id: "DELAY", description: "Delay Send", byteOffset: 36, byteLength: 1, dataRange: [0, 127], initValue: 0, values: null, displayMeasurement: null },
        FLAG1: { id: "FLAG1", description: "Flag1",      byteOffset: 86, byteLength: 1, dataRange: [0, 255], initValue: 0, values: null, displayMeasurement: null },
        MIXER_LEVEL: { id: "MIXER_LEVEL", description: "Mixer Level", byteOffset: 87, byteLength: 1, dataRange: [0, 127], initValue: 100, values: null, displayMeasurement: "dB" }
    }
};

ZenProperties.PARTINFO_SH4D_2 = {
    name: "PartInfoSH4D_2",
    byteLength: 24,
    parameters: {
        STEP_LENGTH:      { id: "STEP_LENGTH",      description: "Step Length",       byteOffset: 0,  byteLength: 1, dataRange: [0, 255], initValue: 0,  values: null, displayMeasurement: "steps", displayModifier: "plusOne" },
        FIRST_STEP:       { id: "FIRST_STEP",       description: "First Step",        byteOffset: 1,  byteLength: 1, dataRange: [0, 255], initValue: 0,  values: null, displayMeasurement: "step" },
        FIRST_STEP_ON:    { id: "FIRST_STEP_ON",    description: "First Step On",     byteOffset: 2,  byteLength: 1, dataRange: [0, 1],   initValue: 0,  values: ["Off", "On"], displayMeasurement: null },
        LAST_STEP:        { id: "LAST_STEP",        description: "Last Step",         byteOffset: 3,  byteLength: 1, dataRange: [0, 255], initValue: 0,  values: null, displayMeasurement: "step", displayModifier: "plusOne" },
        LAST_STEP_ON:     { id: "LAST_STEP_ON",     description: "Last Step On",      byteOffset: 4,  byteLength: 1, dataRange: [0, 1],   initValue: 0,  values: ["Off", "On"], displayMeasurement: null },
        SCALE:            { id: "SCALE",            description: "Scale",             byteOffset: 5,  byteLength: 1, dataRange: [0, 5],   initValue: 0,  values: ["1/8","1/16","1/32","1/4t","1/8t","1/16t"], displayMeasurement: null },
        PLAY_MODE:        { id: "PLAY_MODE",        description: "Play Mode",         byteOffset: 6,  byteLength: 1, dataRange: [0, 4],   initValue: 0,  values: ["Forward","Reverse","Fwd+Rev","Invert","Random"], displayMeasurement: null },
        GATE_LENGTH_OFFSET:{ id: "GATE_LENGTH_OFFSET",description: "Gate Length Offset",byteOffset: 7,  byteLength: 1, dataRange: [0, 127], initValue: 80, values: null, displayMeasurement: null },
        SHUFFLE:          { id: "SHUFFLE",          description: "Shuffle",           byteOffset: 8,  byteLength: 1, dataRange: [0, 127], initValue: 0,  values: null, displayMeasurement: "%" },
        SMOOTH:           { id: "SMOOTH",           description: "Smooth",            byteOffset: 9,  byteLength: 1, dataRange: [0, 2],   initValue: 1,  values: ["Auto","Off","On"], displayMeasurement: null },
        MOTION_RECORDING: { id: "MOTION_RECORDING", description: "Motion Recording",  byteOffset: 10, byteLength: 1, dataRange: [0, 1],   initValue: 0,  values: ["Off","On"], displayMeasurement: null },
        MUTE:             { id: "MUTE",             description: "Mute",              byteOffset: 11, byteLength: 1, dataRange: [0, 1],   initValue: 0,  values: ["Off","On"], displayMeasurement: null }
    }
};

ZenProperties.PARTINFO_SH4D_3 = { name: "PartInfoSH4D_3", byteLength: 12, parameters: {} };
ZenProperties.PARTINFO_SH4D_4 = { name: "PartInfoSH4D_4", byteLength: 84, parameters: {} };

ZenProperties.PATTERN_SH4D = {
    name: "PATTERN_SH4D",
    byteLength: 2360,
    parameters: {
        NAME: {
            id: "NAME", blockName: "NAME", count: 1,
            byteOffset: 0, blockByteLength: 16, byteLength: 16,
            block: ZenProperties.NAME
        },
        PATTERNINFO_SH4D: {
            id: "PATTERNINFO_SH4D", blockName: "PATTERNINFO_SH4D", count: 1,
            byteOffset: 16, blockByteLength: 32, byteLength: 32,
            block: ZenProperties.PATTERNINFO_SH4D
        },
        PARTINFO_SH4D: {
            id: "PARTINFO_SH4D", blockName: "PARTINFO_SH4D", count: 5,
            byteOffset: 48, blockByteLength: 88, byteLength: 440,
            block: ZenProperties.PARTINFO_SH4D
        },
        PARTINFO_SH4D_3: {
            id: "PARTINFO_SH4D_3", blockName: "PARTINFO_SH4D_3", count: 5,
            byteOffset: 505, blockByteLength: 12, byteLength: 60,
            block: ZenProperties.PARTINFO_SH4D_3
        },
        PARTINFO_SH4D_4: {
            id: "PARTINFO_SH4D_4", blockName: "PARTINFO_SH4D_4", count: 5,
            byteOffset: 565, blockByteLength: 84, byteLength: 420,
            block: ZenProperties.PARTINFO_SH4D_4
        },
        PARTINFO_SH4D_2: {
            id: "PARTINFO_SH4D_2", blockName: "PARTINFO_SH4D_2", count: 5,
            byteOffset: 2228, blockByteLength: 24, byteLength: 120,
            block: ZenProperties.PARTINFO_SH4D_2
        }
    }
};

ZenProperties.SH4D_PATCH = {
    name: "SH4D_PATCH",
    byteLength: 2048,
    parameters: {
        header: { id: "header", count: 1, byteOffset: 0,    blockByteLength: 16,   byteLength: 16,   block: ZenProperties.PATC_HEADER },
        tone:   { id: "tone",   count: 1, byteOffset: 16,   blockByteLength: 1632, byteLength: 1632, block: ZenProperties.PCMEX },
        model:  { id: "model",  count: 1, byteOffset: 1648, blockByteLength: 400,  byteLength: 400,  block: { name: "SH4D_MODEL", byteLength: 400, parameters: {} } }
    }
};

// MDLa: PCMEX tone + 412-byte model data + 4-byte footer (unk1, marker, modelType, unk2)
ZenProperties.PCMEX_MODEL = {
    name: "PCMEX_MODEL",
    byteLength: 2048,
    parameters: {
        pcmex: {
            id: "pcmex", count: 1,
            byteOffset: 0, blockByteLength: 1632, byteLength: 1632,
            block: ZenProperties.PCMEX
        },
        model: {
            id: "model",
            description: "Model data",
            byteOffset: 1632,
            byteLength: 412,
            dataRange: null,
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        unk1: {
            id: "unk1",
            description: "Unknown 1",
            byteOffset: 2044,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        marker: {
            id: "marker",
            description: "Marker",
            byteOffset: 2045,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: null,
            values: null,
            displayMeasurement: null
        },
        modelType: {
            id: "modelType",
            description: "Model Type",
            byteOffset: 2046,
            byteLength: 1,
            dataRange: [0, 9],
            initValue: null,
            values: { 0: "mt0", 1: "mt1", 2: "mt2", 3: "mt3", 4: "mt4", 5: "MDL_JP8", 6: "MDL_JU106", 7: "MDL_JX8P", 8: "MDL_SH101", 9: "MDL_JD800" },
            displayMeasurement: null
        },
        unk2: {
            id: "unk2",
            description: "Unknown 2",
            byteOffset: 2047,
            byteLength: 1,
            dataRange: [0, 255],
            initValue: null,
            values: null,
            displayMeasurement: null
        }
    }
};

//---------------------------------------------------------------
// MC101 / MC707 / MV-1 — MCPRJ project structure
//
// MCPRJ holds 8 tracks, each with 17 instrument slots (16 clips + 1 track).
// Several payload types share that 8x17 layout (clipInfo, tones, kits,
// drumkits, looper). Each of those is built as a 2-level descriptor:
//   MC17_<X>   — one row: clip[16] + track (single <X>)
//   MC8x17_<X> — eight rows: track[8] of MC17_<X>
// so accessors read e.g. mcprj.at("tones.track[3].clip[5]") -> a PCMEX,
// or mcprj.at("clipInfo.track[0].track")                    -> the track-level CLIPINFO.
//---------------------------------------------------------------

/**
 * Build a sub-block descriptor describing a single row of 16 clip + 1 track
 * occurrences of `block`.
 */
function _row17(block, name) {
    const sz = block.byteLength;
    return {
        name,
        byteLength: 17 * sz,
        parameters: {
            clip:  { id: "clip",  blockName: name, count: 16, byteOffset: 0,        blockByteLength: sz, byteLength: 16 * sz, block },
            track: { id: "track", blockName: name, count: 1,  byteOffset: 16 * sz,  blockByteLength: sz, byteLength: sz,      block }
        }
    };
}

/**
 * Build a sub-block descriptor describing 8 occurrences of `rowBlock`
 * (the per-row MC17_<X>).
 */
function _grid8x17(rowBlock, name) {
    const sz = rowBlock.byteLength;
    return {
        name,
        byteLength: 8 * sz,
        parameters: {
            track: { id: "track", blockName: name, count: 8, byteOffset: 0, blockByteLength: sz, byteLength: 8 * sz, block: rowBlock }
        }
    };
}

/** A leaf descriptor with NAME_1 at offset 0 plus opaque trailing bytes. */
function _namedLeaf(name, byteLength, description) {
    return {
        name,
        byteLength,
        parameters: {
            NAME_1: {
                id: "NAME_1",
                description: description ?? "Name",
                byteOffset: 0,
                byteLength: 16,
                dataRange: null,
                initValue: null,
                values: null,
                displayMeasurement: null
            }
        }
    };
}

// Leaf placeholders for chunks that aren't fully described elsewhere.
ZenProperties.LOOPER_CLIPINFO = _namedLeaf("LooperClipInfo", 212, "Looper Clip Name");
ZenProperties.ZEN_DRUMINFO = {
    name: "ZenDrumInfo",
    byteLength: 216,
    parameters: {
        cmn:  { id: "cmn",  blockName: "INST_CMN", count: 1, byteOffset: 0,   blockByteLength: 152, byteLength: 152, block: ZenProperties.INST_CMN  },
        pEnv: { id: "pEnv", blockName: "PTL_PENV", count: 1, byteOffset: 152, blockByteLength: 24,  byteLength: 24,  block: ZenProperties.PTL_PENV  },
        fEnv: { id: "fEnv", blockName: "PTL_FENV", count: 1, byteOffset: 176, blockByteLength: 24,  byteLength: 24,  block: ZenProperties.PTL_FENV  },
        aEnv: { id: "aEnv", blockName: "PTL_AENV", count: 1, byteOffset: 200, blockByteLength: 16,  byteLength: 16,  block: ZenProperties.PTL_AENV  }
    }
};
ZenProperties.MCPRJ_U4        = { name: "MCPRJ_U4", byteLength: 12, parameters: {} };

// One drumkit = 88 ZenDrumInfo records.
ZenProperties.ZEN_DRUMKIT = {
    name: "ZenDrumKit",
    byteLength: 88 * 216,
    uiNoRecurse: true,
    parameters: {
        drum: { id: "drum", blockName: "ZenDrumKit", count: 88, byteOffset: 0, blockByteLength: 216, byteLength: 88 * 216, block: ZenProperties.ZEN_DRUMINFO }
    }
};

// Per-row (clip[16] + track) descriptors.
ZenProperties.MC17_PCMEX           = _row17(ZenProperties.PCMEX,           "MC17_PCMEX");
ZenProperties.MC17_PCMR            = _row17(ZenProperties.PCMR,            "MC17_PCMR");
ZenProperties.MC17_CLIPINFO        = _row17(ZenProperties.CLIPINFO,        "MC17_CLIPINFO");
ZenProperties.MC17_ZEN_DRUMKIT     = _row17(ZenProperties.ZEN_DRUMKIT,     "MC17_ZEN_DRUMKIT");
ZenProperties.MC17_LOOPER_CLIPINFO = _row17(ZenProperties.LOOPER_CLIPINFO, "MC17_LOOPER_CLIPINFO");

// Full 8-track grids.
ZenProperties.MC8x17_PCMEX           = _grid8x17(ZenProperties.MC17_PCMEX,           "MC8x17_PCMEX");
ZenProperties.MC8x17_PCMR            = _grid8x17(ZenProperties.MC17_PCMR,            "MC8x17_PCMR");
ZenProperties.MC8x17_CLIPINFO        = _grid8x17(ZenProperties.MC17_CLIPINFO,        "MC8x17_CLIPINFO");
ZenProperties.MC8x17_ZEN_DRUMKIT     = _grid8x17(ZenProperties.MC17_ZEN_DRUMKIT,     "MC8x17_ZEN_DRUMKIT");
ZenProperties.MC8x17_LOOPER_CLIPINFO = _grid8x17(ZenProperties.MC17_LOOPER_CLIPINFO, "MC8x17_LOOPER_CLIPINFO");

// 8-track grids for the smaller per-track-only blocks (no inner clip array).
ZenProperties.MC8_TRACKINFO = {
    name: "MC8_TRACKINFO",
    byteLength: 8 * 256,
    parameters: {
        track: { id: "track", blockName: "MC8_TRACKINFO", count: 8, byteOffset: 0, blockByteLength: 256, byteLength: 8 * 256, block: ZenProperties.TRACKINFO }
    }
};

ZenProperties.MC8_MCPRJ_U4 = {
    name: "MC8_MCPRJ_U4",
    byteLength: 8 * 12,
    parameters: {
        track: { id: "track", blockName: "MC8_MCPRJ_U4", count: 8, byteOffset: 0, blockByteLength: 12, byteLength: 8 * 12, block: ZenProperties.MCPRJ_U4 }
    }
};

// MCPRJ layout (offsets relative to record start):
//   0         NAME_1                  16 bytes  (leaf)
//   16        u1                     496 bytes  (opaque)
//   512       trackInfo             2048 bytes  (8 × TRACKINFO, 256 each)
//   2560      u2                      96 bytes  (8 × 12, opaque)
//   2656      u3                    1664 bytes  (opaque)
//   4320      clipInfo           3211776 bytes  (8 × row of clip[16]+track, 23616 each)
//   3216096   tones               221952 bytes  (8 × row of clip[16]+track, 1632 each)
//   3438048   kits                452608 bytes  (8 × row of clip[16]+track, 3328 each)
//   3890656   drums              2585088 bytes  (8 × row of clip[16]+track, 19008 each)
//   6475744   looper               28832 bytes  (8 × row of clip[16]+track, 212 each)
//   6504576   total
//
// Access: prj.at("trackInfo[2]") → TRACKINFO, prj.at("clipInfo[0].clip[3]") → CLIPINFO
ZenProperties.MCPRJ = {
    name: "MCPRJ",
    byteLength: 6504576,
    uiNoRecurse: true,
    parameters: {
        NAME_1:    { id: "NAME_1",    description: "Project Name", byteOffset: 0,       byteLength: 16,      dataRange: null, initValue: null, values: null, displayMeasurement: null },
        u1:        { id: "u1",        count: 1,  byteOffset: 16,      blockByteLength: 496,    byteLength: 496,     block: { name: "MCPRJ_U1", byteLength: 496,  parameters: {} } },
        trackInfo: { id: "trackInfo", count: 8,  byteOffset: 512,     blockByteLength: 256,    byteLength: 2048,    block: ZenProperties.TRACKINFO },
        u2:        { id: "u2",        count: 8,  byteOffset: 2560,    blockByteLength: 12,     byteLength: 96,      block: { name: "MCPRJ_U4", byteLength: 12,   parameters: {} } },
        u3:        { id: "u3",        count: 1,  byteOffset: 2656,    blockByteLength: 1664,   byteLength: 1664,    block: { name: "MCPRJ_U3", byteLength: 1664, parameters: {} } },
        clipInfo:  { id: "clipInfo",  count: 8,  byteOffset: 4320,    blockByteLength: 401472, byteLength: 3211776, uiNoRecurse: true, block: {
            name: "MCPRJ_ROW_CLIPINFO", byteLength: 401472,uiNoRecurse: true,
            parameters: {
                clip:  { id: "clip",  count: 16, byteOffset: 0,      blockByteLength: 23616, byteLength: 377856, block: ZenProperties.CLIPINFO },
                track: { id: "track", count: 1,  byteOffset: 377856, blockByteLength: 23616, byteLength: 23616,  block: ZenProperties.CLIPINFO }
            }
        }},
        tones:     { id: "tones",     count: 8,  byteOffset: 3216096, blockByteLength: 27744,  byteLength: 221952,  block: {
            name: "MCPRJ_ROW_PCMEX", byteLength: 27744,uiNoRecurse: true,
            parameters: {
                clip:  { id: "clip",  count: 16, byteOffset: 0,     blockByteLength: 1632, byteLength: 26112, block: ZenProperties.PCMEX },
                track: { id: "track", count: 1,  byteOffset: 26112, blockByteLength: 1632, byteLength: 1632,  block: ZenProperties.PCMEX }
            }
        }},
        kits:      { id: "kits",      count: 8,  byteOffset: 3438048, blockByteLength: 56576,  byteLength: 452608,  block: {
            name: "MCPRJ_ROW_PCMR", byteLength: 56576,uiNoRecurse: true,
            parameters: {
                clip:  { id: "clip",  count: 16, byteOffset: 0,     blockByteLength: 3328, byteLength: 53248, block: ZenProperties.PCMR },
                track: { id: "track", count: 1,  byteOffset: 53248, blockByteLength: 3328, byteLength: 3328,  block: ZenProperties.PCMR }
            }
        }},
        drums:     { id: "drums",     count: 8,  byteOffset: 3890656, blockByteLength: 323136, byteLength: 2585088, block: {
            name: "MCPRJ_ROW_ZEN_DRUMKIT", byteLength: 323136,uiNoRecurse: true,
            parameters: {
                clip:  { id: "clip",  count: 16, byteOffset: 0,      blockByteLength: 19008, byteLength: 304128, block: ZenProperties.ZEN_DRUMKIT },
                track: { id: "track", count: 1,  byteOffset: 304128, blockByteLength: 19008, byteLength: 19008,  block: ZenProperties.ZEN_DRUMKIT }
            }
        }},
        looper:    { id: "looper",    count: 8,  byteOffset: 6475744, blockByteLength: 3604,   byteLength: 28832,   block: {
            name: "MCPRJ_ROW_LOOPER", byteLength: 3604,
            parameters: {
                clip:  { id: "clip",  count: 16, byteOffset: 0,    blockByteLength: 212, byteLength: 3392, block: ZenProperties.LOOPER_CLIPINFO },
                track: { id: "track", count: 1,  byteOffset: 3392, blockByteLength: 212, byteLength: 212,  block: ZenProperties.LOOPER_CLIPINFO }
            }
        }}
    }
};

//---------------------------------------------------------------
// MC101 / MC707 / MV-1 — MCUSR user-sound library
//
// MCUSR layout (offsets relative to record start):
//   0          tones     104448 bytes (64 × PCMEX,       1632 each)
//   104448     kits      212992 bytes (64 × PCMR,        3328 each)
//   317440     drums    1216512 bytes (64 × ZEN_DRUMKIT, 19008 each)
//   1533952    samples    42000 bytes (500 × SAMPLE_INFO,   84 each)
//   1575952    u1         67584 bytes (128 × 528)
//   1643536    total
//
// Access: mcusr.at("tones[3]") → PCMEX, mcusr.at("samples[12]") → SAMPLE_INFO
//---------------------------------------------------------------

ZenProperties.MCUSR = {
    name: "MCUSR",
    byteLength: 1643536,
    uiNoRecurse: true,
    parameters: {
        tones:   { id: "tones",   uiNoRecurse: true, count: 64,  byteOffset: 0,       blockByteLength: 1632,  byteLength: 104448,  block: ZenProperties.PCMEX       },
        kits:    { id: "kits",    uiNoRecurse: true, count: 64,  byteOffset: 104448,  blockByteLength: 3328,  byteLength: 212992,  block: ZenProperties.PCMR        },
        drums:   { id: "drums",   uiNoRecurse: true, count: 64,  byteOffset: 317440,  blockByteLength: 19008, byteLength: 1216512, block: ZenProperties.ZEN_DRUMKIT  },
        samples: { id: "samples", uiNoRecurse: true, count: 500, byteOffset: 1533952, blockByteLength: 84,    byteLength: 42000,   block: ZenProperties.SAMPLE_INFO  },
        u1:      { id: "u1",      uiNoRecurse: true, count: 128, byteOffset: 1575952, blockByteLength: 528,   byteLength: 67584,   block: { name: "MCUSR_U1", byteLength: 528, parameters: {} } }
    }
};

ZenProperties.FILE_HEADER = {
    name: "FILE_HEADER",
    byteLength: 16,
    parameters: {
        FILE_TYPE: { id: "FILE_TYPE", count: 1, byteOffset: "fileType",  },
        FILE_SUB_TYPE: { id: "FILE_SUB_TYPE", count: 1, byteOffset: "fileSubType",  },
        MODEL_CODE: { id: "MODEL_CODE", count: 1, byteOffset: "modelCode"  }
    }
};
export default ZenProperties;
